import {
  Box,
  Button,
  Typography,
  useTheme,
  List,
  ListItem,
  ListItemText,
  Grid,
} from "@mui/material";

import { tokens } from "../../theme/tokens";
import heroImage from "../../assets/sunset.png";
import undraw2 from "../../assets/undraw_collaborating_mayd_gold.svg";

const LandingPage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const Section = ({ children }) => (
    <Box
      my={6}
      p={4}
      backgroundColor="background.paper"
      borderRadius="8px"
    >
      {children}
    </Box>
  );

  const IllustrationPlaceholder = ({ title, description }) => (
    <Box
      sx={{
        border: "1px dashed",
        borderColor: colors.grey?.[500] || "grey.500",
        borderRadius: "8px",
        p: 3,
        minHeight: "220px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        textAlign: "center",
        opacity: 0.8,
      }}
    >
      <Typography
        variant="h6"
        sx={{ color: colors.houndGold?.[500] }}
      >
        {title}
      </Typography>

      <Typography variant="body2" mt={1}>
        {description}
      </Typography>
    </Box>
  );

  return (
    <Box>
      <Box
  sx={{
    minHeight: { xs: "620px", md: "760px" },
    borderRadius: "16px",
    overflow: "hidden",
    position: "relative",
    display: "flex",
    alignItems: "center",
    px: { xs: 3, md: 8 },
    py: { xs: 8, md: 12 },
    backgroundImage: `
      linear-gradient(
        90deg,
        rgba(0, 0, 0, 0.78) 0%,
        rgba(0, 0, 0, 0.55) 38%,
        rgba(0, 0, 0, 0.15) 70%,
        rgba(0, 0, 0, 0.05) 100%
      ),
      url(${heroImage})
    `,
    backgroundSize: "cover",
    backgroundPosition: "center",
    boxShadow: "0 24px 80px rgba(0,0,0,0.35)",
  }}
>
  <Box sx={{ maxWidth: "620px" }}>
    <Typography
  variant="overline"
  sx={{
    color: colors.scalpelTeal[500],
    letterSpacing: "0.18em",
    fontWeight: 700,
  }}
>
  OPERATIONAL CLARITY
</Typography>
    <Typography
      variant="h1"
      sx={{
        fontSize: { xs: "3rem", md: "5.5rem" },
        lineHeight: 0.95,
        fontWeight: 700,
        color: "#fff",
        mb: 3,
      }}
    >
      Your business shouldn't stop because one person is off.
    </Typography>

    <Typography
      variant="h5"
      sx={{
        color: "rgba(255,255,255,0.88)",
        maxWidth: "540px",
        lineHeight: 1.5,
        mb: 4,
      }}
    >
      Reduce dependency on key people by turning operational knowledge into clear systems your whole team can follow.
      <br/><br />
      Whether someone is on holiday, off sick, leaves the business, or simply isn't available, your team should still know what to do.
    </Typography>

    <Button
      variant="contained"
      color="secondary"
      size="large"
      href="/contact"
    >
      Book an Operational Review
    </Button>
  </Box>
</Box>

    <Section>
  <Typography variant="h4" gutterBottom sx={{ color: colors.houndGold?.[500] }}>
    Growth creates hidden operational risk.
  </Typography>

  <Typography variant="body1" mb={3}>
    Most businesses don't suddenly become disorganised.
    <br />
    <br />
    It happens gradually.
  </Typography>

  <List>
    <ListItem disableGutters dense>
      <ListItemText primary="One person becomes the expert." />
    </ListItem>

    <ListItem disableGutters dense>
      <ListItemText primary="Another creates their own way of doing things." />
    </ListItem>

    <ListItem disableGutters dense>
      <ListItemText primary="Processes drift." />
    </ListItem>

    <ListItem disableGutters dense>
      <ListItemText primary="Knowledge spreads across emails, WhatsApp, spreadsheets and conversations." />
    </ListItem>
  </List>

  <Typography variant="body1" mt={3}>
    Before long, your business depends on people remembering instead of systems
    supporting them.
    <br />
    <br />
    Everything still works...
    <br />
    <br />
    ...until it doesn't.
  </Typography>
</Section>

<Section>
  <Typography variant="h4" gutterBottom sx={{ color: colors.houndGold?.[500] }}>
    Operational Clarity helps you answer questions like:
  </Typography>

  <List>
    <ListItem disableGutters dense>
      <ListItemText primary="If someone leaves tomorrow, what knowledge leaves with them?" />
    </ListItem>

    <ListItem disableGutters dense>
      <ListItemText primary="Which parts of the business rely on just one person?" />
    </ListItem>

    <ListItem disableGutters dense>
      <ListItemText primary="Which processes are undocumented?" />
    </ListItem>

    <ListItem disableGutters dense>
      <ListItemText primary="Where are managers constantly being interrupted?" />
    </ListItem>

    <ListItem disableGutters dense>
      <ListItemText primary="Which teams are working differently without realising it?" />
    </ListItem>

    <ListItem disableGutters dense>
      <ListItemText primary="Where is operational friction slowing growth?" />
    </ListItem>
  </List>

  <Typography variant="body1" mt={3}>
    These aren't technology problems.
    <br />
    <br />
    They're visibility problems.
  </Typography>
</Section>

<Section>
  <Grid container spacing={4} alignItems="center">
    <Grid item xs={12} md={5}>
      <Box
        sx={{
          backgroundColor: colors.scalpelTeal[500],
          borderRadius: "12px",
          p: 4,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          border: "1px solid rgba(255,255,255,0.05)",
        }}
      >
        <img
          src={undraw2}
          alt="Workflow Illustration"
          style={{
            width: "100%",
            maxWidth: "340px",
            opacity: 0.95,
          }}
        />
      </Box>
    </Grid>

    <Grid item xs={12} md={7}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ color: colors.houndGold?.[500] }}
      >
        Our approach
      </Typography>

      <Typography variant="body1" mb={3}>
        We begin by understanding how your business actually operates today.
        <br />
        <br />
        Together we identify:
      </Typography>

      <List>
        <ListItem disableGutters dense>
          <ListItemText primary="Critical processes" />
        </ListItem>

        <ListItem disableGutters dense>
          <ListItemText primary="Operational bottlenecks" />
        </ListItem>

        <ListItem disableGutters dense>
          <ListItemText primary="Hidden dependencies" />
        </ListItem>

        <ListItem disableGutters dense>
          <ListItemText primary="Undocumented knowledge" />
        </ListItem>

        <ListItem disableGutters dense>
          <ListItemText primary="Unclear ownership" />
        </ListItem>

        <ListItem disableGutters dense>
          <ListItemText primary="Opportunities to simplify" />
        </ListItem>
      </List>

      <Typography variant="body1" mt={3}>
        From there we build practical systems that make the business easier to
        run.
        <br />
        <br />
        No unnecessary bureaucracy.
        <br />
        No 200-page consultancy reports.
        <br />
        Just clearer operations.
      </Typography>
    </Grid>
  </Grid>
</Section>

<Section>
  <Typography variant="h4" gutterBottom sx={{ color: colors.houndGold?.[500] }}>
    The outcome
  </Typography>

  <Typography variant="body1" mb={3}>
    Imagine being able to...
  </Typography>

  <List>
    <ListItem disableGutters dense>
      <ListItemText primary="✔ Take a holiday without constant phone calls." />
    </ListItem>

    <ListItem disableGutters dense>
      <ListItemText primary="✔ Onboard new staff faster." />
    </ListItem>

    <ListItem disableGutters dense>
      <ListItemText primary="✔ Know exactly who owns what." />
    </ListItem>

    <ListItem disableGutters dense>
      <ListItemText primary="✔ Reduce mistakes caused by inconsistent processes." />
    </ListItem>

    <ListItem disableGutters dense>
      <ListItemText primary="✔ Build a business that becomes stronger as it grows - not more chaotic." />
    </ListItem>
  </List>

  <Typography variant="body1" mt={3}>
    That's what Operational Clarity is designed to help you achieve.
  </Typography>
</Section>

<Section>
  <Typography variant="h4" gutterBottom sx={{ color: colors.houndGold?.[500] }}>
    Let's uncover the hidden risk.
  </Typography>

  <Typography variant="body1" mb={3}>
    If you've ever thought:
    <br />
    <br />
    "Only Sarah knows how to do that."
    <br />
    <br />
    or
    <br />
    <br />
    "I'll just do it myself because it's quicker."
    <br />
    <br />
    then there's probably hidden operational risk worth uncovering.
  </Typography>

  <Button variant="contained" color="secondary" size="large" href="/contact">
    Let's have a conversation
  </Button>
</Section>

<Box mt={8} textAlign="center">
  <Typography variant="body2">Barry Marples - Operational Clarity</Typography>

  <Typography variant="caption" display="block" mt={2}>
    Clarity in operations • Alignment in teams • Confidence in decisions
  </Typography>
</Box>
    </Box>
  );
};

export default LandingPage;